<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";

import BaseCheckbox from "@/components/ui/BaseCheckbox.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";

import type { TreeNodeData, TreeNodeInstance } from "./types";

const props = withDefaults(
  defineProps<{
    node: TreeNodeData;
    indentation: number;
    isFirstNode?: boolean;
    selectedNodeId: string | null;
    setNodeRef: (id: string, ref: TreeNodeInstance | null) => void;
    showCheckboxes?: boolean;
    expandedByDefault?: boolean;
  }>(),
  { showCheckboxes: false, expandedByDefault: false },
);

const emit = defineEmits<{
  selectNode: [node: TreeNodeData];
  toggleCheckbox: [node: TreeNodeData, checked: boolean];
}>();

const isExpanded = ref(props.expandedByDefault);
const hasChildren = computed(() => props.node.children.length > 0);
const isSelected = computed(() => !props.showCheckboxes && props.node.id === props.selectedNodeId);
const isDisabled = computed(() => props.node.disabled);
const isFirstNode = computed(() => props.isFirstNode);

const treeNodeRef = useTemplateRef("treeNode");

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};
defineExpose({
  expand: () => {
    isExpanded.value = true;
  },
});

const arrow = computed(() => (isExpanded.value ? "arrow/chevron-down" : "arrow/chevron-right"));

const selectNode = () => {
  if (isDisabled.value) return;

  if (props.showCheckboxes) {
    // In multi-select mode, toggle the checkbox
    handleCheckboxChange(!props.node.checked);
  } else {
    // In single-select mode, emit select event
    emit("selectNode", props.node);
  }
};

const handleCheckboxChange = (checked: boolean) => {
  emit("toggleCheckbox", props.node, checked);
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!treeNodeRef.value) return;

  const currentNode = event.target as HTMLElement;
  const focusableNodes = Array.from(document.querySelectorAll<HTMLElement>('[role="treeitem"]'));
  const currentIndex = focusableNodes.indexOf(currentNode);

  switch (event.key) {
    case "ArrowRight":
      if (hasChildren.value && !isExpanded.value) {
        toggleExpand();
      } else if (hasChildren.value && isExpanded.value) {
        focusableNodes[currentIndex + 1]?.focus();
      }
      break;

    case "ArrowLeft":
      if (hasChildren.value && isExpanded.value) {
        toggleExpand();
      } else if (currentIndex > 0) {
        focusableNodes[currentIndex - 1]?.focus();
      }
      break;

    case "ArrowUp":
      if (currentIndex > 0) {
        focusableNodes[currentIndex - 1]?.focus();
      }
      break;

    case "ArrowDown":
      if (currentIndex < focusableNodes.length - 1) {
        focusableNodes[currentIndex + 1]?.focus();
      }
      break;

    case "Enter":
    case " ":
      event.preventDefault();
      selectNode();
      break;

    case "Home":
      focusableNodes[0]?.focus();
      break;

    case "End":
      focusableNodes[focusableNodes.length - 1]?.focus();
      break;
  }
};

const tabindex = computed(() => (isSelected.value || isFirstNode.value ? 0 : -1));

const INDENTATION_STEP = 28; // 20px (icon) + 8px (padding)
</script>

<template>
  <!-- eslint does not recognize dynamic tabindex assignment -->
  <!-- eslint-disable-next-line vuejs-accessibility/interactive-supports-focus -->
  <div
    ref="treeNode"
    :class="['tree-node', { 'tree-node-selected': isSelected }]"
    role="treeitem"
    v-bind="hasChildren && { 'aria-expanded': isExpanded }"
    :aria-selected="isSelected"
    :aria-disabled="isDisabled"
    :data-testid="`tree-node-${node.label}`"
    :tabindex
    @click="selectNode"
    @keydown="handleKeydown"
  >
    <div class="tree-node-content" :style="{ paddingLeft: `${indentation}px` }">
      <button
        v-if="hasChildren"
        class="tree-node-expand-button"
        :aria-label="isExpanded ? 'Collapse' : 'Expand'"
        :data-testid="`expand-button-${node.label}`"
        tabindex="-1"
        @click.stop="toggleExpand"
      >
        <SvgIcon
          :name="arrow"
          class="collapse-icon"
          :class="{ expanded: isExpanded }"
          width="20"
          height="20"
        />
      </button>
      <div v-else class="collapse-placeholder" />

      <SvgIcon
        v-if="node.icon"
        :name="node.icon"
        class="tree-node-icon"
        role="presentation"
        width="20"
        height="20"
      />

      <span class="tree-node-label">
        {{ node.label }}
      </span>

      <BaseCheckbox
        v-if="showCheckboxes"
        :checked="node.checked"
        :indeterminate="node.indeterminate"
        :disabled="isDisabled"
        class="tree-node-checkbox"
        @change="handleCheckboxChange"
      />
    </div>
  </div>

  <!-- Children Nodes -->
  <div v-if="hasChildren && isExpanded" role="group">
    <TreeNode
      v-for="child in node.children"
      :ref="(ref) => setNodeRef(child.id, ref as TreeNodeInstance)"
      :key="child.id"
      :node="child"
      :selected-node-id
      :indentation="indentation + INDENTATION_STEP"
      :set-node-ref
      :show-checkboxes
      :expanded-by-default
      @select-node="emit('selectNode', $event)"
      @toggle-checkbox="(node, checked) => emit('toggleCheckbox', node, checked)"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/font";
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";

.tree-node {
  cursor: pointer;
  width: 100%;
  outline: revert;
  color: colors.$text;

  .tree-node-content {
    display: flex;
    align-items: center;
    padding: 0.5rem 16px 0.5rem 0;
    gap: 8px;
    width: 100%;

    .collapse-placeholder {
      flex-shrink: 0;
      width: 20px;
    }
  }

  .tree-node-expand-button {
    display: flex;
    align-items: center;
    padding: 0;
    cursor: pointer;

    .collapse-icon {
      color: colors.$icon-subtle;
      transition: transform 100ms;
    }
  }

  .tree-node-icon {
    flex-shrink: 0;
    color: colors.$icon-subtle;
  }

  &:not(.tree-node-selected, [aria-disabled="true"]):hover {
    background-color: colors.$background-grey;
    border-radius: 4px;
  }

  &-selected {
    color: colors.$text-inverse;
    border-radius: 4px;
    background-color: colors.$main;

    .tree-node-expand-button .collapse-icon,
    .tree-node-icon {
      color: colors.$icon-inverse;
    }
  }

  &[aria-disabled="true"] {
    color: colors.$text-disabled;
    cursor: default;

    .tree-node-expand-button .collapse-icon,
    .tree-node-icon {
      color: colors.$icon-disabled;
    }
  }

  .tree-node-label {
    @include utils.ellipsis;

    font-size: font.$size-normal;
    font-weight: font.$weight-medium;
    flex-grow: 1;
  }

  .tree-node-checkbox {
    margin-left: auto;
  }
}
</style>
